import type { AnalyticsHomeBlock } from "../api/types";

/**
 * G-25 — one shop's slice of the home page's figures, and the tiles' totals.
 *
 * G-169 (WO-D): both come from the server's one answer (`GET /analytics/home`,
 * `AnalyticsService.homeSummary`), per shop and added once there at full
 * precision. This module only turns the decimal strings into the numbers the
 * page formats and compares — it adds nothing up (before it, `sumShopRows`
 * re-added each shop's rounded figures in floats, a second copy of the rule).
 *
 * WO-B (OD-198): a figure the page could not load is unknown — the whole
 * answer fails, so the page holds `null` rows and shows "—", never `0`.
 */
export interface ShopMoneyRow {
  locationId: string;
  name: string;
  revenueEur: number;
  saleCount: number;
  /** G-45 — the contribution with the IVA inside goods prices removed. */
  contributionAfterVatEur: number;
  /** WO-B part 5 (OD-198): sales holding stock of unknown cost. */
  unknownCostSaleCount: number;
  wasteEur: number;
  /** WO-B part 6: waste entries of unknown cost, left out of `wasteEur`. */
  wasteUnknownCostEntryCount: number;
  ordersDue: number;
  /** G-32's daily nut for this shop — recurring costs only (OD-16); null for a manager. */
  dailyCostEur: number | null;
  /** G-204: this shop's running costs for the period; null = unknown (none on record) or not the owner. */
  runningCostsEur: number | null;
  /** G-204: contribution after VAT less this shop's running costs; null while the costs are unknown. */
  profitEur: number | null;
  /** Revenue ÷ tickets; null with no ticket. */
  averageSaleEur: number | null;
  /** This shop's share of the period's revenue, in percent. */
  sharePercent: number;
}

export type ShopMoneyTotals = Pick<
  ShopMoneyRow,
  | "revenueEur"
  | "saleCount"
  | "contributionAfterVatEur"
  | "unknownCostSaleCount"
  | "wasteEur"
  | "wasteUnknownCostEntryCount"
  | "runningCostsEur"
  | "profitEur"
>;

/** The server's rows as the page reads them, in the server's order. */
export function toShopRows(
  block: AnalyticsHomeBlock,
  ordersDue: (locationId: string) => number,
): ShopMoneyRow[] {
  return block.byLocation.map((row) => ({
    locationId: row.locationId,
    name: row.locationName,
    revenueEur: Number(row.totalEur),
    saleCount: row.saleCount,
    contributionAfterVatEur: Number(row.contributionAfterVatEur),
    unknownCostSaleCount: row.salesWithUnknownCostCount,
    wasteEur: Number(row.wasteCostEur),
    wasteUnknownCostEntryCount: row.wasteUnknownCostEntryCount,
    ordersDue: ordersDue(row.locationId),
    dailyCostEur: row.dailyFixedCostEur == null ? null : Number(row.dailyFixedCostEur),
    runningCostsEur: row.runningCostsEur == null ? null : Number(row.runningCostsEur),
    profitEur: row.profitEur == null ? null : Number(row.profitEur),
    averageSaleEur: row.averageSaleEur == null ? null : Number(row.averageSaleEur),
    sharePercent: Number(row.revenueSharePercent),
  }));
}

/** The tiles' totals — the server's sum, not one made here. */
export function toShopTotals(block: AnalyticsHomeBlock): ShopMoneyTotals {
  return {
    revenueEur: Number(block.totals.totalEur),
    saleCount: block.totals.saleCount,
    contributionAfterVatEur: Number(block.totals.contributionAfterVatEur),
    unknownCostSaleCount: block.totals.salesWithUnknownCostCount,
    wasteEur: Number(block.totals.wasteCostEur),
    wasteUnknownCostEntryCount: block.totals.wasteUnknownCostEntryCount,
    runningCostsEur: block.totals.runningCostsEur == null ? null : Number(block.totals.runningCostsEur),
    profitEur: block.totals.profitEur == null ? null : Number(block.totals.profitEur),
  };
}
